<template>
  <SectionPageShell
    section="domains"
    title="Domains"
    subtitle="Registrierung und Laufzeit Ihrer Domains im Überblick"
  >
    <DataTable
      :value="domainStore.items"
      :loading="domainStore.loading"
      :rows="20"
      :totalRecords="domainStore.items.length"
      paginator
      dataKey="id"
      paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
      :rowsPerPageOptions="[10,20,50]"
      currentPageReportTemplate="Zeige {first} bis {last} von {totalRecords}"
      responsiveLayout="scroll"
    >
      <template #empty>
        Für diese Organisation sind keine Domains hinterlegt.
      </template>

      <TableColumn field="full_name" header="Domain" sortable>
        <template #body="{data}">
          <a :href="domainUrl(data.full_name)" target="_blank" rel="noopener noreferrer">
            {{ data.full_name }}
          </a>
        </template>
      </TableColumn>

      <TableColumn header="Status">
        <template #body="{data}">
          <span class="badge" :class="statusMeta(data).className">
            {{ statusMeta(data).label }}
          </span>
        </template>
      </TableColumn>

      <TableColumn field="start_at" header="Leistungsbeginn" sortable>
        <template #body="{data}">
          {{ formatDate(data.start_at) }}
        </template>
      </TableColumn>

      <TableColumn field="billing_period_end_on" header="Laufzeit bis" sortable>
        <template #body="{data}">
          {{ formatDate(data.billing_period_end_on) }}
        </template>
      </TableColumn>

      <TableColumn field="end_at" header="Vertragsende" sortable>
        <template #body="{data}">
          {{ formatDate(data.end_at) }}
        </template>
      </TableColumn>

      <TableColumn header="Aktionen">
        <template #body="{data}">
          <button
            type="button"
            class="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-2"
            @click="openContacts(data)"
          >
            <Icon icon="fa-pro-solid:address-card" />
            Kontaktdaten
          </button>
        </template>
      </TableColumn>
    </DataTable>

    <Dialog
      v-model:visible="contactDialogVisible"
      modal
      :header="contactDialogTitle"
      :style="{width: 'min(56rem, 95vw)'}"
    >
      <div v-if="domainStore.contactLoading" class="contact-state">
        <Icon icon="fa-pro-solid:spinner" class="contact-spinner" />
        Kontaktdaten werden geladen …
      </div>

      <div v-else-if="contactError" class="alert alert-warning mb-0" role="alert">
        {{ contactError }}
      </div>

      <div v-else-if="contacts.length === 0" class="contact-state">
        Für diese Domain sind keine Kontaktdaten verfügbar.
      </div>

      <div v-else class="row g-3">
        <div v-for="(contact, index) in contacts" :key="`${contact.allocation}-${index}`" class="col-12 col-md-6">
          <article class="contact-card h-100">
            <div class="d-flex align-items-center justify-content-between gap-2 mb-3">
              <h3 class="contact-card__title mb-0">{{ allocationLabel(contact.allocation) }}</h3>
              <span class="badge text-bg-light">{{ contactTypeLabel(contact.type) }}</span>
            </div>

            <div v-if="contact.organization" class="fw-semibold">{{ contact.organization }}</div>
            <div>{{ contact.name }}</div>

            <div v-if="hasAddress(contact)" class="contact-card__section">
              <div v-for="streetLine in contact.street" :key="streetLine">{{ streetLine }}</div>
              <div>{{ [contact.postal_code, contact.city].filter(Boolean).join(' ') }}</div>
              <div v-if="contact.state">{{ contact.state }}</div>
              <div v-if="contact.country">{{ contact.country.toUpperCase() }}</div>
            </div>

            <div class="contact-card__section">
              <div v-if="contact.email_address">
                <span class="contact-card__label">E-Mail:</span>
                <a :href="`mailto:${contact.email_address}`">{{ contact.email_address }}</a>
              </div>
              <div v-if="contact.phone_number">
                <span class="contact-card__label">Telefon:</span>
                <a :href="telephoneHref(contact.phone_number)">{{ contact.phone_number }}</a>
              </div>
              <div v-if="contact.fax_number">
                <span class="contact-card__label">Fax:</span>
                {{ contact.fax_number }}
              </div>
            </div>
          </article>
        </div>
      </div>
    </Dialog>
  </SectionPageShell>
</template>

<script>
import dayjs from "dayjs";
import Dialog from "primevue/dialog";
import SectionPageShell from "@/components/base_type/SectionPageShell.vue";
import {useDomainsStore} from "@/stores/domains";

export default {
  name: "DomainsIndex",
  components: {
    Dialog,
    SectionPageShell,
  },
  data() {
    return {
      contactDialogVisible: false,
      selectedDomain: null,
      contacts: [],
      contactError: null,
    }
  },
  setup() {
    return {
      domainStore: useDomainsStore(),
    }
  },
  created() {
    this.domainStore.index()
  },
  computed: {
    contactDialogTitle() {
      return this.selectedDomain ? `Kontaktdaten für ${this.selectedDomain.full_name}` : 'Kontaktdaten'
    },
  },
  methods: {
    openContacts(domain) {
      this.selectedDomain = domain
      this.contacts = []
      this.contactError = null
      this.contactDialogVisible = true

      this.domainStore.show(domain.id).then(({data}) => {
        if (this.selectedDomain?.id === domain.id) {
          this.contacts = data.contacts || []
        }
      }).catch((error) => {
        if (this.selectedDomain?.id === domain.id) {
          this.contactError = error.response?.data?.message || 'Die aktuellen Kontaktdaten konnten nicht geladen werden.'
        }
      })
    },
    domainUrl(fullName) {
      return `https://${fullName}`
    },
    formatDate(value) {
      return value ? dayjs(value).format('DD.MM.YYYY') : '-'
    },
    allocationLabel(allocation) {
      return {
        owner: 'Domaininhaber',
        admin: 'Administrativer Kontakt',
        tech: 'Technischer Kontakt',
        zone: 'Zonenkontakt',
      }[allocation] || 'Kontakt'
    },
    contactTypeLabel(type) {
      return {
        person: 'Person',
        org: 'Organisation',
        role: 'Rolle',
      }[type] || 'Kontakt'
    },
    hasAddress(contact) {
      return contact.street?.length || contact.postal_code || contact.city || contact.state || contact.country
    },
    telephoneHref(phoneNumber) {
      return `tel:${phoneNumber.replace(/\s/g, '')}`
    },
    statusMeta(domain) {
      switch (domain.status) {
        case 'terminated':
          return {label: 'Gekündigt', className: 'text-bg-warning'}
        case 'deleted':
        case 'finished':
        case 'cancelled':
        case 'canceled':
          return {label: 'Beendet', className: 'text-bg-secondary'}
        case 'draft':
        case 'in_progress':
          return {label: 'In Vorbereitung', className: 'text-bg-info'}
        default:
          return {label: 'Aktiv', className: 'text-bg-success'}
      }
    },
  },
}
</script>

<style scoped>
.contact-state {
  align-items: center;
  color: var(--bs-secondary);
  display: flex;
  gap: 0.75rem;
  justify-content: center;
  min-height: 8rem;
}

.contact-spinner {
  animation: contact-spin 1s linear infinite;
}

.contact-card {
  border: 1px solid var(--bs-border-color);
  border-radius: 0.75rem;
  overflow-wrap: anywhere;
  padding: 1rem;
}

.contact-card__title {
  font-size: 1rem;
}

.contact-card__section {
  margin-top: 1rem;
}

.contact-card__label {
  display: inline-block;
  font-weight: 600;
  margin-right: 0.35rem;
}

@keyframes contact-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
